import { OpCol } from "components/customAntd/DLS/OpCol/OpCol";
import { IOnSubmitArgs, OpForm } from "components/customAntd/DLS/OpForm/OpForm";
import { OpRow } from "components/customAntd/DLS/OpRow/OpRow";
import { DATE_TIME_AM_PM_FORMAT } from "constants/dates";
import dayjs from "dayjs";
import { formatFullName, statusKeyMap } from "utils/utils";
import { getStatusColor, getStatusNameById } from "utils/visitorsHelper";
import { OpCard } from "components/customAntd/DLS/OpCard/OpCard";
import { useState } from "react";
import {
  InfoCircleOutlined,
  PrinterOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { OpModal } from "components/customAntd/DLS/OpModal/OpModal";
import { OpButton } from "components/customAntd/DLS/OpButton/OpButton";
import { useSelector } from "react-redux";
import { RootState } from "store/store";
import STATUS from "constants/status";
import { useConfirmModal } from "utils/customHooks/useConfirmModal";
import { postRequest } from "api/apiClient";
import { List, notification } from "antd";
import { DenyDetails } from "types/denyTypes";
import { Kiosk } from "types/kioskTypes";
import { OpDivider } from "components/customAntd/DLS/OpDivider/OpDivider";
import { useTranslation } from "react-i18next";
import i18n from "i18n/i18n";

import {
  printBadge,
  formatVisitorName,
  formatVisitDate,
  BadgeData,
} from "utils/badgePrinting";

interface ProfileTabProps {
  visitorPhotoUrl: string | null;
  denyDetails?: DenyDetails;
  connectedKiosks: Kiosk[];
}

const ProfileTab: React.FC<ProfileTabProps> = ({
  visitorPhotoUrl,
  denyDetails,
  connectedKiosks,
}) => {
  const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
  const auth = useSelector((state: RootState) => state.auth.auth);
  const selectedOrgName = auth.data[0].tokenScopeList.find(
    (scope) => Number(scope.org.id) === Number(orgId),
  )?.org.name;
  const { selectedVisit, selectedVisitor } = useSelector(
    (state: RootState) => state.visits,
  );
  const { invitationConfig } = useSelector(
    (state: RootState) => state.visitorInvitation,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [printBadgeForm] = OpForm.useForm();

  const [isVisitorPhotoModalOpen, setIsVisitorPhotoModalVisible] =
    useState(false);
  const [badgePrintModalOpen, setBadgePrintModalOpen] = useState(false);
  const [isDeniedEntryModalOpen, setIsDeniedEntryModalOpen] = useState(false);

  const confirmModal = useConfirmModal();

  const { t } = useTranslation();

  const initialValues = {
    fullName: formatFullName(
      selectedVisitor?.firstName!,
      selectedVisitor?.middleName!,
      selectedVisitor?.lastName!,
    ),
    status: t(
      statusKeyMap[
      getStatusNameById(
        selectedVisitor?.status!,
        selectedVisit?.scheduleStart!,
      )
      ] || "",
    ),
    email: selectedVisitor?.email,
    mobilePhone: selectedVisitor?.mobilePhone,
    signIn: selectedVisitor?.signIn
      ? dayjs(selectedVisitor.signIn).format(DATE_TIME_AM_PM_FORMAT)
      : undefined,
    signOut: selectedVisitor?.signOut
      ? dayjs(selectedVisitor.signOut).format(DATE_TIME_AM_PM_FORMAT)
      : undefined,
  };

  const resendInvitation = () => {
    confirmModal({
      title: t("visitors.profile.confirmResend.title"),
      content: t("visitors.profile.confirmResend.message"),
      okText: t("visitors.profile.confirmResend.ok"),
      cancelText: t("visitors.profile.confirmResend.cancel"),
      onOk: () => {
        try {
          postRequest(
            `/orgs/${orgId}/visitor/${selectedVisit?.id}/invitationEmail?language=${i18n.language.split("-")[0]}`,
            {
              visitorId: selectedVisitor?.id,
            },
          );
          notification.success({
            message: t("notification.success"),
            description: t("visitors.profile.notification.resendSuccess"),
            placement: "bottomRight",
          });
        } catch {
          notification.error({
            message: t("notification.error"),
            description: t("visitors.profile.notification.resendError"),
            placement: "bottomRight",
          });
        }
      },
    });
  };

  const handleStatusClick = () => {
    if (selectedVisitor?.status === STATUS.DENIED_ENTRY.id) {
      setIsDeniedEntryModalOpen(true);
    }
  };

  const handlePrintBadge = async ({ values }: IOnSubmitArgs<any>) => {
    const selectedKiosk = values.kioskSelect;

    if (!selectedKiosk) {
      notification.error({
        message: t("notification.error"),
        description: t("visitors.profile.notification.selectKioskError"),
        placement: "bottomRight",
      });
      return;
    }

    setIsSubmitting(true); // Disable button to prevent multiple clicks

    try {
      await postRequest(
        `/orgs/${orgId}/visitor/${selectedVisit?.id}/visitors/${selectedVisitor?.id}/printBadge`,
        { deviceId: selectedKiosk },
      );

      notification.success({
        message: t("notification.success"),
        description: t("visitors.profile.notification.printSuccess"),
        placement: "bottomRight",
      });

      setBadgePrintModalOpen(false);
    } catch (error) {
      console.error("Failed to print badge:", error);
      notification.error({
        message: t("notification.error"),
        description: t("visitors.profile.notification.printError"),
        placement: "bottomRight",
      });
    } finally {
      setIsSubmitting(false); // Re-enable the button
    }
  };

  const handleBrowserPrintBadge = async () => {
    const webPrintLanguage = "en";
    const badgeData: BadgeData = {
      visitorType:
        selectedVisit?.visitorType?.name ||
        t("sco_data_visitor", { lng: webPrintLanguage }),
      visitorName: formatVisitorName(
        selectedVisitor?.firstName || null,
        selectedVisitor?.middleName || null,
        selectedVisitor?.lastName || null,
      ),
      visitorCompany: selectedVisit?.company || "",
      hostLabel: t("visitors.table.host.label", { lng: webPrintLanguage }),
      hostName: selectedVisit?.host?.firstName
        ? `${selectedVisit.host.firstName} ${selectedVisit.host.lastName || ""}`.trim()
        : "",
      orgName: selectedOrgName || "",
      visitDate: formatVisitDate(selectedVisit?.scheduleStart || null),
    };
    setIsSubmitting(true);

    try {
      await printBadge(badgeData);
    } catch (error) {
      console.error("Failed to print badge via browser:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <OpForm
        onSubmit={() => console.log()}
        hasError={false}
        defaultButtons={false}
        initialValues={initialValues}
        isReadOnly={true}
      >
        <OpRow gutter={24}>
          <OpCol span={12}>
            <OpForm.Input
              readOnly
              label={t("visitors.profile.form.fullName")}
              name="fullName"
            />
            <OpRow gutter={16}>
              <OpCol
                span={
                  selectedVisitor?.status === STATUS.DENIED_ENTRY.id ? 21 : 24
                }
              >
                <OpForm.Input
                  readOnly
                  label={t("visitors.profile.form.status")}
                  name="status"
                  style={{
                    backgroundColor: getStatusColor(
                      getStatusNameById(
                        selectedVisitor?.status!,
                        selectedVisit?.scheduleStart!,
                      ),
                    ),
                    color: "white",
                    WebkitTextFillColor: "white",
                    opacity: 1,
                  }}
                />
              </OpCol>
              {selectedVisitor?.status === STATUS.DENIED_ENTRY.id && (
                <OpCol span={3}>
                  <OpButton
                    style={{ width: "30px", marginTop: "30px" }}
                    icon={<InfoCircleOutlined />}
                    onClick={handleStatusClick}
                  />
                </OpCol>
              )}
            </OpRow>

            <OpForm.Input
              readOnly
              label={t("visitors.profile.form.mobilePhone")}
              name="mobilePhone"
            />
          </OpCol>
          <OpCol span={12}>
            <OpCard
              hoverable={!!visitorPhotoUrl}
              style={{
                width: "100%",
                height: "280px",
                borderRadius: "30px",
                overflow: "hidden",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
              onClick={() => {
                if (visitorPhotoUrl) {
                  setIsVisitorPhotoModalVisible(true);
                }
              }}
            >
              {visitorPhotoUrl ? (
                <img
                  src={visitorPhotoUrl}
                  alt="Visitor"
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    borderRadius: "30px",
                  }}
                  onError={(e) => { }}
                />
              ) : (
                <UserOutlined style={{ fontSize: "64px", color: "#ccc" }} />
              )}
            </OpCard>
          </OpCol>
        </OpRow>
        <OpRow gutter={16}>
          <OpCol flex="auto">
            <OpForm.Input
              readOnly
              label={t("visitors.profile.form.email")}
              name="email"
            />
          </OpCol>
          <OpCol flex="none">
            <OpButton
              style={{ marginTop: "30px" }}
              onClick={resendInvitation}
              disabled={
                selectedVisitor?.status !== STATUS.PENDING.id ||
                  !initialValues.email ||
                  !invitationConfig?.sendEmail ||
                  (selectedVisit?.scheduleStart &&
                    selectedVisit?.scheduleEnd &&
                    !dayjs().isBetween(
                      selectedVisit.scheduleStart,
                      selectedVisit.scheduleEnd,
                      null,
                      "[]",
                    ) &&
                    dayjs(selectedVisit.scheduleStart).isBefore(dayjs(), "day"))
                  ? true
                  : false
              }
            >
              {t("visitors.profile.form.resendInvitation")}
            </OpButton>
          </OpCol>
        </OpRow>
        <OpRow gutter={16}>
          <OpCol span={12}>
            <OpForm.Input
              readOnly
              label={t("visitors.profile.form.signIn")}
              name="signIn"
            />
          </OpCol>
          <OpCol span={12}>
            <OpForm.Input
              readOnly
              label={t("visitors.profile.form.signOut")}
              name="signOut"
            />
          </OpCol>
        </OpRow>
        {selectedVisitor?.status !== STATUS.DENIED_ENTRY.id && (
          <OpRow gutter={12} style={{ marginTop: "10px" }}>
            <OpCol>
              <OpButton
                onClick={handleBrowserPrintBadge}
                disabled={isSubmitting}
                icon={<PrinterOutlined />}
              >
                {t("visitors.profile.form.printBadge")}
              </OpButton>
            </OpCol>
            {connectedKiosks.length > 0 && (
              <OpCol>
                <OpButton
                  onClick={() => setBadgePrintModalOpen(true)}
                  disabled={isSubmitting}
                  icon={<PrinterOutlined />}
                >
                  {t("visitors.profile.form.printBadgeViaKiosk")}
                </OpButton>
              </OpCol>
            )}
          </OpRow>
        )}
      </OpForm>

      {badgePrintModalOpen && (
        <OpModal
          title={t("visitors.profile.modal.badgePrintTitle")}
          open={badgePrintModalOpen}
          onOk={() => {
            printBadgeForm.submit();
          }}
          onCancel={() => setBadgePrintModalOpen(false)}
          okButtonProps={{ disabled: isSubmitting }}
          cancelButtonProps={{ disabled: isSubmitting }}
        >
          <OpDivider />

          <OpForm
            form={printBadgeForm}
            initialValues={{
              kioskSelect:
                connectedKiosks.length > 0 ? connectedKiosks[0].id : undefined,
            }}
            onSubmit={handlePrintBadge}
            hasError={false}
            defaultButtons={false}
          >
            <OpForm.Select
              name="kioskSelect"
              options={connectedKiosks.map((kiosk) => ({
                label: kiosk.label,
                value: kiosk.id,
              }))}
            />
          </OpForm>
        </OpModal>
      )}

      {isVisitorPhotoModalOpen && (
        <OpModal
          open={isVisitorPhotoModalOpen}
          title={t("visitors.profile.modal.visitorPhotoTitle")}
          centered
          onCancel={() => setIsVisitorPhotoModalVisible(false)} // This handles closing when "X" is clicked
          onClose={() => setIsVisitorPhotoModalVisible(false)} // This handles closing with custom buttons
          footer={[
            <OpButton
              key="close"
              type="primary"
              onClick={() => setIsVisitorPhotoModalVisible(false)}
            >
              {t("visitors.profile.modal.close")}
            </OpButton>,
          ]}
        >
          {visitorPhotoUrl && (
            <img
              src={visitorPhotoUrl}
              alt="Visitor"
              style={{ width: "100%" }}
            />
          )}
        </OpModal>
      )}

      {isDeniedEntryModalOpen && (
        <OpModal
          open={isDeniedEntryModalOpen}
          title={t("visitors.profile.modal.deniedEntryTitle")}
          centered
          onCancel={() => setIsDeniedEntryModalOpen(false)}
          footer={[
            <OpButton
              key="close"
              type="primary"
              onClick={() => setIsDeniedEntryModalOpen(false)}
            >
              {t("visitors.profile.modal.close")}
            </OpButton>,
          ]}
        >
          <List
            bordered
            dataSource={[
              {
                title: t("visitors.profile.modal.reasonForDenial"),
                value: denyDetails?.reason?.name || "---",
              },
              {
                title: t("visitors.profile.modal.effectiveDate"),
                value: denyDetails?.deniedAt
                  ? dayjs(denyDetails?.deniedAt)
                    .utc(true)
                    .local()
                    .format(DATE_TIME_AM_PM_FORMAT)
                  : "---",
              },
            ]}
            renderItem={(item) => (
              <List.Item>
                <strong>{item.title}</strong> <span>{item.value}</span>
              </List.Item>
            )}
          />
        </OpModal>
      )}
    </>
  );
};

export default ProfileTab;
